import React, { createContext, useContext, useEffect, useState } from "react";
import api, { fmtErr, setReadOnly } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = anon
    const [loading, setLoading] = useState(true);
    const isReviewer = !!user && user.role === "External Reviewer";

    useEffect(() => { setReadOnly(isReviewer); }, [isReviewer]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/auth/me");
                setUser(data);
            } catch {
                setUser(false);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Sliding session: while signed in and actively working, quietly renew the 24h token so it never
    // expires mid-task. Renewal stops after ~20 min of no interaction (or when the tab is hidden).
    useEffect(() => {
        if (!user) return undefined;
        let last = Date.now();
        const bump = () => { last = Date.now(); };
        const events = ["mousedown", "keydown", "touchstart", "scroll"];
        events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
        const iv = setInterval(async () => {
            if (document.visibilityState !== "visible") return;
            if (Date.now() - last > 20 * 60 * 1000) return;
            try { const { data } = await api.post("/auth/refresh"); if (data?.token) localStorage.setItem("qube_token", data.token); } catch { /* ignore transient refresh failures */ }
        }, 15 * 60 * 1000);
        return () => { clearInterval(iv); events.forEach((e) => window.removeEventListener(e, bump)); };
    }, [user]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.mfa_required) return { ok: true, mfa: data };
            localStorage.setItem("qube_token", data.token);
            setUser(data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            return { ok: false, error: fmtErr(e.response?.data?.detail) || e.message };
        }
    };

    const completeSession = (data) => {
        localStorage.setItem("qube_token", data.token);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout request failed; clearing session locally", e); }
        localStorage.removeItem("qube_token");
        setUser(false);
    };

    return (
        <AuthCtx.Provider value={{ user, loading, login, logout, setUser, completeSession, isReviewer }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
